import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Siren, Mail, Clock } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO, HOURS_WEEK, HOURS_TIME, HEADER_SUNSET } from "@/lib/site";

const SLOGAN_CYCLE = [
  "There's Always a Solution.",
  "Serving Christiana",
  "Serving Murfreesboro",
  "Serving Shelbyville",
  "Serving Smyrna",
  "Serving Rockvale",
  "Serving Bell Buckle",
  "Serving Eagleville",
  "Serving Chapel Hill",
  "Serving Memphis",
];

function TopBarSlogan({ idx, sizeClass = "text-3xl", compact = false }) {
  return (
    <>
      <span key={`cross-${idx}`} data-testid={compact ? "topbar-cross-fade-compact" : "header-cross-fade"} aria-hidden="true" className="header-cross-fade">
        <svg width={compact ? 34 : 150} height={compact ? 44 : 195} viewBox="0 0 34 44">
          <path d="M17 4 V40 M6 16 H28" stroke="#FFFFFF" strokeWidth={compact ? 3.5 : 2.5} strokeLinecap="round" fill="none" />
        </svg>
      </span>
      <span
        key={idx}
        data-testid={compact ? "topbar-slogan-compact" : "topbar-slogan"}
        aria-hidden="true"
        className={`header-slogan-run font-script ${sizeClass} text-gold-champagne`}
      >
        {SLOGAN_CYCLE[idx]}
      </span>
    </>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [cityIdx, setCityIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCityIdx((i) => (i + 1) % SLOGAN_CYCLE.length), 10000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div data-testid="top-info-bar" className="hidden lg:flex relative overflow-hidden items-center justify-between bg-[#0D0D10] border-b border-gold/15 px-8 py-2.5 text-xs text-zinc-400">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <img src={HEADER_SUNSET} alt="" className="w-full h-full object-cover opacity-[0.3]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/45 to-[#0A0A0C]/70" />
        </div>
        <div className="relative flex items-center gap-6">
          <a href={PHONE_TEL} data-testid="topbar-phone-link" className="flex items-center gap-2 hover:text-gold-champagne transition-colors">
            <Phone size={12} className="text-gold" /> {PHONE}
          </a>
          <a href={EMAIL_MAILTO} data-testid="topbar-email-link" className="flex items-center gap-2 hover:text-gold-champagne transition-colors">
            <Mail size={12} className="text-gold" /> {EMAIL}
          </a>
        </div>
        <div className="relative hidden min-[1600px]:flex flex-1 items-center justify-center overflow-hidden h-full mx-6">
          <TopBarSlogan idx={cityIdx} sizeClass="text-base" compact />
        </div>
        <span className="relative flex items-center gap-2 font-mono-label tracking-[0.15em] uppercase text-[10px]">
          <Clock size={12} className="text-gold" /> {HOURS_WEEK} · {HOURS_TIME} · After-Hours Emergency Available
        </span>
      </div>

      <header data-testid="site-header" className="sticky top-0 z-50 bg-[#0A0A0C]/85 backdrop-blur-md border-b border-gold/20 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <img src={HEADER_SUNSET} alt="" className="w-full h-full object-cover opacity-[0.3]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/45 via-[#0A0A0C]/35 to-[#0A0A0C]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 h-[120px] flex items-center justify-between">
          <div className="flex flex-col items-start justify-center">
            <Link to="/" data-testid="header-logo-link" aria-label="Redeemer Solutions LLC home">
              <Logo className="h-16 sm:h-24" />
            </Link>
            <span
              key={`phone-${cityIdx}`}
              data-testid="header-phone-fade"
              aria-hidden="true"
              className="header-phone-fade text-gold-gradient font-mono-label text-[11px] tracking-[0.22em] mt-1.5"
            >
              {PHONE}
            </span>
          </div>
          <div data-testid="header-slogan-center" className="min-[1600px]:hidden flex-1 relative overflow-hidden h-full flex items-center justify-center">
            <TopBarSlogan idx={cityIdx} sizeClass="text-2xl" />
          </div>
          <nav className="hidden min-[1600px]:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors ${
                    isActive ? "text-gold" : "text-zinc-300 hover:text-gold-champagne"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="min-[1600px]:hidden text-[#F4EFE6] p-2 shrink-0 ml-3"
            aria-label="Toggle navigation menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {open && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-[60] bg-[#0A0A0C]/[0.985] backdrop-blur-xl flex flex-col">
          <div className="flex items-center justify-between px-5 h-[120px] border-b border-gold/15">
            <Logo className="h-16" />
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#F4EFE6] p-2" aria-label="Close menu">
              <X size={24} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-8 pt-8 pb-32 flex flex-col gap-1" aria-label="Mobile">
            {NAV_LINKS.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-display text-xl py-2.5 border-b border-gold/10 transition-colors ${
                    isActive ? "text-gold" : "text-[#F4EFE6] hover:text-gold-champagne"
                  }`
                }
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/schedule"
              data-testid="mobile-nav-schedule-link"
              onClick={() => setOpen(false)}
              className="font-display text-xl py-2.5 border-b border-gold/10 text-[#F4EFE6] hover:text-gold-champagne transition-colors"
            >
              Schedule Appointment
            </NavLink>
            <a
              href={PHONE_TEL}
              data-testid="mobile-menu-call-button"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold px-6 py-4 rounded-sm"
            >
              <Phone size={16} /> Call {PHONE}
            </a>
            <a
              href={PHONE_TEL}
              data-testid="mobile-menu-emergency-button"
              className="inline-flex items-center justify-center gap-2 border border-red-500/40 text-red-400 font-medium px-6 py-4 rounded-sm"
            >
              <Siren size={16} /> 24/7 Emergency Service
            </a>
          </nav>
        </div>
      )}
    </>
  );
}
